// Design-bridge primitives (RFC-104 P3).
//
// v1 (Phase 1): Button, Surface, Stack, Text, Field, IconButton, Tooltip,
//               Toast, Spinner, Slider, SplitPane, Dock.
//
// v1.1 (Phase 2 foundation dispatch — this file): adds Icon, DomainGlyph,
// VisuallyHidden, SkipLink, SkipLinks, KeyHint, PluginViewport, Banner,
// Stepper, Stat, Tabs, ConfirmDialog, OrgSelector, Drawer, Dialog, Card,
// Chip, Badge, Callout. Existing IconButton (40 px touch target), SplitPane
// (40 px hit-target separator with keyboard ARIA), Tooltip (portal), Toast
// (severity="error" rejected per anti-pattern A17) are fixed in place.
//
// Each primitive is a thin React component with token-driven styling via
// inline styles (CSS-in-JS lib choice deferred per RFC-104 P3 risk-3 to
// @ux-prototype-engineer's bundle-size measurement). Inline styles is the
// simplest path that works today; the swap to vanilla-extract is local to
// this file when the bundle measurement is in.
//
// Source-of-truth citations:
//   - geosoft-platform-design-system/docs/DESIGN_POLICY.md §5
//   - geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md
//   - APPLICATION_PATH/design/30-engineering/frontend-architect/design-system-consumption.md §3
//   - APPLICATION_PATH/design/40-quality/accessibility-qa/full-audit/findings.md §H.1 (SplitPane fix)

import {
  forwardRef,
  useEffect,
  useLayoutEffect as _useLayoutEffect,
  useRef,
  type CSSProperties,
  type ForwardedRef,
  type HTMLAttributes,
  type ReactNode,
  useId,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// SSR-safe layout effect: useLayoutEffect on the client (avoids paint flicker)
// + useEffect on the server (avoids the React SSR warning fired by tests).
const useIsoLayoutEffect = typeof document !== 'undefined' ? _useLayoutEffect : useEffect;
import { tokens, type SemanticColor, type SurfaceLevel } from './tokens';
import { useReducedMotion } from './hooks';

// Re-export Phase-2 primitives from sub-files. Public surface stays
// `import { ... } from '@/design'` (or `@/design/primitives`) — sub-files
// are an implementation detail, NOT a new public path. Keeping the existing
// 12 primitives in this file keeps the Phase-1 test fixtures stable.
export {
  Icon,
  type IconProps,
  type IconName,
  type IconSize,
  ICON_REGISTRY,
} from './primitives/Icon';
export {
  DomainGlyph,
  type DomainGlyphProps,
  type DomainGlyphName,
  DOMAIN_GLYPH_REGISTRY,
} from './primitives/DomainGlyph';
export { VisuallyHidden, type VisuallyHiddenProps } from './primitives/VisuallyHidden';
export {
  SkipLink,
  SkipLinks,
  type SkipLinkProps,
  type SkipLinksProps,
} from './primitives/SkipLink';
export { KeyHint, type KeyHintProps } from './primitives/KeyHint';
export {
  PluginViewport,
  type PluginViewportProps,
  type PluginMountFn,
} from './primitives/PluginViewport';
export {
  Banner,
  type BannerProps,
  type BannerSeverity,
  type BannerAction,
} from './primitives/Banner';
export { Stepper, type StepperProps, type StepperStep } from './primitives/Stepper';
export { Stat, type StatProps } from './primitives/Stat';
export { Tabs, type TabsProps, type TabsTab } from './primitives/Tabs';
export {
  ConfirmDialog,
  type ConfirmDialogProps,
} from './primitives/ConfirmDialog';
export { OrgSelector, type OrgSelectorProps, type Org } from './primitives/OrgSelector';
export { Drawer, type DrawerProps, type DrawerSide } from './primitives/Drawer';
export { Dialog, type DialogProps, type DialogAction } from './primitives/Dialog';
export { Card, type CardProps } from './primitives/Card';
export { Chip, type ChipProps, type ChipVariant } from './primitives/Chip';
export { Badge, type BadgeProps } from './primitives/Badge';
export { Callout, type CalloutProps } from './primitives/Callout';
export { Menu, type MenuProps, type MenuItemDef } from './primitives/Menu';

// ---------- Common helpers ------------------------------------------------

type DivProps = HTMLAttributes<HTMLDivElement>;

interface PrimitiveTestProps {
  /** Test id consumed by Playwright + axe specs. */
  'data-testid'?: string;
}

function durationOrZero(ms: number, reduced: boolean): number {
  return reduced ? tokens.motion.reducedMotion.duration : ms;
}

// ---------- Button --------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    PrimitiveTestProps {
  variant?: ButtonVariant;
  children: ReactNode;
}

const buttonStyles: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: tokens.color.accent.primary,
    color: tokens.color.accent.on,
    border: '1px solid transparent',
  },
  secondary: {
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `1px solid ${tokens.color.surface[3]}`,
  },
  ghost: {
    background: 'transparent',
    color: tokens.color.ink.primary,
    border: '1px solid transparent',
  },
  danger: {
    background: tokens.color.semantic.danger,
    color: tokens.color.ink.inverse,
    border: '1px solid transparent',
  },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', style, children, ...rest },
  ref,
) {
  const reduced = useReducedMotion();
  const merged: CSSProperties = {
    ...buttonStyles[variant],
    padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
    borderRadius: tokens.radius.md,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.md,
    fontWeight: tokens.font.weight.medium,
    cursor: 'pointer',
    transitionProperty: 'background, transform, opacity',
    transitionDuration: `${durationOrZero(tokens.motion.duration.fast, reduced)}ms`,
    transitionTimingFunction: tokens.motion.easing.standard,
    ...style,
  };
  return (
    <button ref={ref} style={merged} {...rest}>
      {children}
    </button>
  );
});

// ---------- Surface -------------------------------------------------------

export interface SurfaceProps extends DivProps, PrimitiveTestProps {
  level?: SurfaceLevel;
  elevation?: 0 | 1 | 2 | 3;
  children?: ReactNode;
}

export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(function Surface(
  { level = 1, elevation = 0, style, children, ...rest },
  ref,
) {
  const merged: CSSProperties = {
    background: tokens.color.surface[level],
    color: tokens.color.ink.primary,
    boxShadow: tokens.elevation[elevation],
    borderRadius: tokens.radius.md,
    ...style,
  };
  return (
    <div ref={ref} style={merged} {...rest}>
      {children}
    </div>
  );
});

// ---------- Stack ---------------------------------------------------------

export interface StackProps extends DivProps, PrimitiveTestProps {
  direction?: 'row' | 'column';
  gap?: keyof typeof tokens.space;
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
  children?: ReactNode;
}

export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  { direction = 'column', gap = 2, align, justify, style, children, ...rest },
  ref,
) {
  const merged: CSSProperties = {
    display: 'flex',
    flexDirection: direction,
    gap: tokens.space[gap],
    alignItems: align,
    justifyContent: justify,
    ...style,
  };
  return (
    <div ref={ref} style={merged} {...rest}>
      {children}
    </div>
  );
});

// ---------- Text ----------------------------------------------------------

export type TextVariant = 'title' | 'subtitle' | 'body' | 'caption' | 'code';

export interface TextProps
  extends HTMLAttributes<HTMLSpanElement>,
    PrimitiveTestProps {
  variant?: TextVariant;
  as?: keyof JSX.IntrinsicElements;
  children?: ReactNode;
}

const textStyles: Record<TextVariant, CSSProperties> = {
  title: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xl,
    fontWeight: tokens.font.weight.semibold,
    lineHeight: tokens.font.lineHeight.tight,
    color: tokens.color.ink.primary,
  },
  subtitle: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.lg,
    fontWeight: tokens.font.weight.medium,
    lineHeight: tokens.font.lineHeight.tight,
    color: tokens.color.ink.primary,
  },
  body: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.md,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.normal,
    color: tokens.color.ink.primary,
  },
  caption: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.normal,
    color: tokens.color.ink.muted,
  },
  code: {
    fontFamily: tokens.font.family.mono,
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.regular,
    lineHeight: tokens.font.lineHeight.normal,
    color: tokens.color.ink.primary,
  },
};

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { variant = 'body', as = 'span', style, children, ...rest },
  ref: ForwardedRef<HTMLElement>,
) {
  const Tag = as as 'span';
  const merged: CSSProperties = { ...textStyles[variant], ...style };
  return (
    <Tag
      ref={ref as unknown as ForwardedRef<HTMLSpanElement>}
      style={merged}
      {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
    >
      {children}
    </Tag>
  );
});

// ---------- Field ---------------------------------------------------------

export interface FieldProps extends PrimitiveTestProps {
  label: string;
  error?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
}

export function Field({ label, error, children, 'data-testid': testId }: FieldProps) {
  const id = useId();
  const errId = `${id}-error`;
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}>
      <label htmlFor={id} style={{ ...textStyles.caption, color: tokens.color.ink.secondary }}>
        {label}
      </label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errId : undefined,
      })}
      {error && (
        <span id={errId} style={{ ...textStyles.caption, color: tokens.color.semantic.danger }}>
          {error}
        </span>
      )}
    </div>
  );
}

// ---------- IconButton ----------------------------------------------------
// Phase-2 fix: closes DESIGN_POLICY.md §5 "40px minimum touch target" and the
// supervisor finding on the prior 24×24 size. The icon glyph slot stays at
// the playbook-default 20px (consumers pass `<Icon size={20} />`); the touch
// target itself is `tokens.density.touchMin` (40px). Adds a visible
// :focus-visible ring resolved via tokens.focus.* per a11y-plan.md §0.1 #2.

export interface IconButtonProps extends ButtonProps {
  /** Required: SR label since the button is icon-only. */
  'aria-label': string;
  /** Optional: render as 32 px (dense rows) instead of the default 40 px touch target.
   *  Use only inside a `<ListRow density="dense">` per design-system-consumption.md §3.
   *  Default is the platform 40 px floor; consumers should not bypass it lightly. */
  dense?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { children, style, dense, ...rest },
  ref,
) {
  const target = dense ? tokens.density.rowDense : tokens.density.touchMin;
  const merged: CSSProperties = {
    minWidth: target,
    minHeight: target,
    width: target,
    height: target,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.sm,
    ...style,
  };
  return (
    <Button
      ref={ref}
      variant="ghost"
      style={merged}
      {...(rest as Omit<IconButtonProps, 'dense'>)}
    >
      {children}
    </Button>
  );
});

// ---------- Tooltip -------------------------------------------------------
// Phase-2 fix: tooltip body is rendered in a portal at document.body so the
// trigger DOM remains intact (closes the inline-styled wrapper finding).
// ARIA contract preserved: outer trigger gets aria-describedby pointing at
// the rendered tooltip's id; tooltip carries role="tooltip".

export interface TooltipProps extends PrimitiveTestProps {
  content: string;
  children: ReactNode;
  /** Delay in ms before the tooltip appears. */
  delay?: number;
}

export function Tooltip({ content, children, delay = 200, 'data-testid': testId }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion();
  const effectiveDelay = durationOrZero(delay, reduced);
  const tooltipId = useId();
  const timeoutRef = useRef<number | undefined>(undefined);

  useIsoLayoutEffect(() => {
    if (!visible || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top + window.scrollY - tokens.space[1],
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, [visible]);

  useEffect(() => () => {
    if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
  }, []);

  function show() {
    timeoutRef.current = window.setTimeout(() => setVisible(true), effectiveDelay);
  }
  function hide() {
    if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current);
    setVisible(false);
  }

  const portalReady = typeof document !== 'undefined';

  return (
    <span
      ref={triggerRef}
      data-testid={testId}
      aria-describedby={visible ? tooltipId : undefined}
      style={{ display: 'inline-block' }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible && portalReady && coords
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              style={{
                position: 'absolute',
                top: coords.top,
                left: coords.left,
                transform: 'translate(-50%, -100%)',
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                background: tokens.color.overlay.tooltip,
                color: tokens.color.ink.primary,
                borderRadius: tokens.radius.sm,
                fontSize: tokens.font.size.xs,
                zIndex: tokens.zIndex.tooltip,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

// ---------- Toast ---------------------------------------------------------
// Phase-2 contract: toasts are info / success / warning ONLY.
// `severity="error"` (or "danger") is rejected per anti-pattern A17 — errors
// must use `<Banner severity="error|critical">` so the user can see the cause
// + recovery action persistently. We keep the old `status` prop name for
// backward compatibility with Phase-1 consumers; danger now throws in dev,
// downgrades to warning in prod with a console.error.

export type ToastStatus = Exclude<SemanticColor, 'danger'>;

export interface ToastProps extends PrimitiveTestProps {
  status: ToastStatus;
  children: ReactNode;
  onDismiss?: () => void;
}

export function Toast({ status, children, onDismiss, 'data-testid': testId }: ToastProps) {
  // Anti-pattern A17 guard: reject error severity at runtime.
  // Why typed `as ToastStatus`-only at compile time is not enough: existing
  // consumers were typed against `SemanticColor` (which includes 'danger').
  if ((status as SemanticColor) === 'danger') {
    const isDev = typeof import.meta !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
    if (isDev) {
      throw new Error(
        'Toast: severity="danger"/"error" is rejected per anti-pattern A17. ' +
          'Use <Banner severity="error|critical"> instead. See ' +
          'design/00-discovery/boss-synthesis.md §13 anti-pattern A17.',
      );
    }
    console.error(
      '[geosoft] Toast severity="error" rejected (A17). Use <Banner>. Downgrading to warning.',
    );
    status = 'warning' as ToastStatus;
  }
  return (
    <div
      role={status === 'warning' ? 'alert' : 'status'}
      aria-live={status === 'warning' ? 'assertive' : 'polite'}
      data-testid={testId}
      style={{
        background: tokens.color.surface[2],
        borderLeft: `3px solid ${tokens.color.semantic[status]}`,
        color: tokens.color.ink.primary,
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        borderRadius: tokens.radius.md,
        boxShadow: tokens.elevation[2],
        zIndex: tokens.zIndex.toast,
        display: 'flex',
        gap: tokens.space[2],
        alignItems: 'center',
      }}
    >
      <span>{children}</span>
      {onDismiss && (
        <IconButton aria-label="Dismiss notification" onClick={onDismiss}>
          ×
        </IconButton>
      )}
    </div>
  );
}

// ---------- Spinner -------------------------------------------------------

export type SpinnerSize = 'sm' | 'md' | 'lg';

const spinnerSize: Record<SpinnerSize, number> = { sm: 12, md: 20, lg: 32 };

export interface SpinnerProps extends PrimitiveTestProps {
  size?: SpinnerSize;
  'aria-label'?: string;
}

export function Spinner({ size = 'md', 'data-testid': testId, 'aria-label': ariaLabel = 'Loading' }: SpinnerProps) {
  const reduced = useReducedMotion();
  const dim = spinnerSize[size];
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        display: 'inline-block',
        width: dim,
        height: dim,
        border: `2px solid ${tokens.color.surface[3]}`,
        borderTopColor: tokens.color.accent.primary,
        borderRadius: tokens.radius.pill,
        animation: reduced ? 'none' : 'geosoft-spinner-rotate 0.8s linear infinite',
      }}
    />
  );
}

// ---------- Slider --------------------------------------------------------

export interface SliderProps extends PrimitiveTestProps {
  min: number;
  max: number;
  value: number;
  step?: number;
  'aria-label': string;
  onChange(next: number): void;
}

export function Slider({ min, max, value, step = 1, onChange, 'aria-label': ariaLabel, 'data-testid': testId }: SliderProps) {
  return (
    <input
      type="range"
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      data-testid={testId}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.currentTarget.value))}
      style={{
        width: '100%',
        accentColor: tokens.color.accent.primary,
      }}
    />
  );
}

// ---------- SplitPane -----------------------------------------------------
// Phase-2 fix: closes A11y QA §H.1 (BLOCKING — WCAG 2.5.8 Target Size).
// The separator is now the 40 px hit-target itself perpendicular to its
// orientation, with a centred 1 px visible rule painted with
// `tokens.color.surface[3]`. Adds tabIndex={0} + aria-orientation +
// aria-valuenow/min/max/text per a11y-plan.md §H.2.

export interface SplitPaneProps extends PrimitiveTestProps {
  orientation?: 'vertical' | 'horizontal';
  left: ReactNode;
  right: ReactNode;
  /** Initial split percent (0..100); kept simple in v1 — drag-to-resize is a follow-up. */
  initialSplit?: number;
  /** Optional aria-label on the outer landmark; default 'Split pane'. */
  'aria-label'?: string;
  /** Optional aria-label on the separator; default 'Resize panes'. */
  separatorLabel?: string;
}

export function SplitPane({
  orientation = 'vertical',
  left,
  right,
  initialSplit = 50,
  'data-testid': testId,
  'aria-label': ariaLabel = 'Split pane',
  separatorLabel = 'Resize panes',
}: SplitPaneProps) {
  const isVertical = orientation === 'vertical';
  const flexDirection = isVertical ? 'row' : 'column';
  const [pct, setPct] = useState<number>(() => Math.max(10, Math.min(90, initialSplit)));

  function nudge(delta: number) {
    setPct((prev) => Math.max(10, Math.min(90, Math.round(prev + delta))));
  }

  function onSeparatorKeyDown(e: React.KeyboardEvent<HTMLSpanElement>) {
    const arrowDecrease = isVertical ? 'ArrowLeft' : 'ArrowUp';
    const arrowIncrease = isVertical ? 'ArrowRight' : 'ArrowDown';
    if (e.key === arrowDecrease) {
      e.preventDefault();
      nudge(e.shiftKey ? -10 : -1);
    } else if (e.key === arrowIncrease) {
      e.preventDefault();
      nudge(e.shiftKey ? 10 : 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setPct(10);
    } else if (e.key === 'End') {
      e.preventDefault();
      setPct(90);
    }
  }

  // 40 px hit-target perpendicular to the orientation; 1 px visible rule
  // centred inside via a non-interactive `aria-hidden` child painted with
  // `tokens.color.surface[3]`.
  const separatorOuter: CSSProperties = isVertical
    ? {
        flex: `0 0 ${tokens.density.touchMin}px`,
        height: '100%',
        cursor: 'col-resize',
        background: 'transparent',
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
      }
    : {
        flex: `0 0 ${tokens.density.touchMin}px`,
        width: '100%',
        cursor: 'row-resize',
        background: 'transparent',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'stretch',
      };
  const separatorRule: CSSProperties = isVertical
    ? {
        width: tokens.border.width.thin,
        height: '100%',
        background: tokens.color.surface[3],
      }
    : {
        height: tokens.border.width.thin,
        width: '100%',
        background: tokens.color.surface[3],
      };

  return (
    <main
      role="main"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection,
        width: '100%',
        height: '100%',
      }}
    >
      <section
        role="region"
        aria-label="Pane A"
        style={{ flex: `0 0 ${pct}%`, position: 'relative' }}
      >
        {left}
      </section>
      <span
        role="separator"
        aria-orientation={isVertical ? 'vertical' : 'horizontal'}
        aria-label={separatorLabel}
        aria-valuenow={pct}
        aria-valuemin={10}
        aria-valuemax={90}
        aria-valuetext={`${pct}%`}
        tabIndex={0}
        onKeyDown={onSeparatorKeyDown}
        style={separatorOuter}
      >
        <span aria-hidden="true" style={separatorRule} />
      </span>
      <section
        role="region"
        aria-label="Pane B"
        style={{ flex: '1 1 auto', position: 'relative' }}
      >
        {right}
      </section>
    </main>
  );
}

// ---------- Dock ----------------------------------------------------------

export interface DockProps extends PrimitiveTestProps {
  side: 'left' | 'right';
  collapsed?: boolean;
  collapsedWidth?: number;
  expandedWidth?: number;
  children: ReactNode;
  'aria-label': string;
}

export function Dock({
  side,
  collapsed = false,
  collapsedWidth = 64,
  expandedWidth = 256,
  children,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: DockProps) {
  const reduced = useReducedMotion();
  return (
    <aside
      role="complementary"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        width: collapsed ? collapsedWidth : expandedWidth,
        background: tokens.color.surface[2],
        borderLeft: side === 'right' ? `1px solid ${tokens.color.surface[3]}` : undefined,
        borderRight: side === 'left' ? `1px solid ${tokens.color.surface[3]}` : undefined,
        transitionProperty: 'width',
        transitionDuration: `${durationOrZero(tokens.motion.duration.normal, reduced)}ms`,
        transitionTimingFunction: tokens.motion.easing.standard,
        zIndex: tokens.zIndex.dock,
        overflow: 'hidden',
      }}
    >
      {children}
    </aside>
  );
}

// ---------- Spinner keyframes + global focus ring (single global insert) ----

// Per a11y-plan.md §0.1 #2 every interactive element gets a visible focus ring
// via :focus-visible. Tokens: focus.ringWidth, focus.ringOffset, focus.ringColor.
// Inserted once at module load. Phase-3 may move this to a CSS layer.
if (typeof document !== 'undefined' && !document.getElementById('geosoft-design-globals')) {
  const style = document.createElement('style');
  style.id = 'geosoft-design-globals';
  style.textContent = `
@keyframes geosoft-spinner-rotate { to { transform: rotate(360deg); } }

button:focus-visible,
[role="button"]:focus-visible,
a:focus-visible,
[role="link"]:focus-visible,
[role="menuitem"]:focus-visible,
[role="tab"]:focus-visible,
[role="option"]:focus-visible,
[role="separator"]:focus-visible,
[role="slider"]:focus-visible,
input:focus-visible,
select:focus-visible,
textarea:focus-visible,
[tabindex]:focus-visible {
  outline: ${tokens.focus.ringWidth}px solid ${tokens.focus.ringColor};
  outline-offset: ${tokens.focus.ringOffset}px;
}

[data-geosoft-skip-link] {
  /* Visible only on :focus-visible per Google convention.
     See: design-system-consumption.md §3 + a11y-plan.md §A.3. */
  position: absolute;
  clip-path: inset(50%);
  width: ${tokens.visuallyHidden.size}px;
  height: ${tokens.visuallyHidden.size}px;
  overflow: hidden;
  white-space: nowrap;
}
[data-geosoft-skip-link]:focus,
[data-geosoft-skip-link]:focus-visible {
  clip-path: none;
  width: auto;
  height: auto;
  overflow: visible;
  white-space: normal;
}
`;
  document.head.appendChild(style);
}
